import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import "../../css/estudiantes.css";
import "../../css/CursosMultiSelect.css";
import academicService from "../../services/academicService";
import courseService from "../../services/courseService";
import asignaturaService from "../../services/asignaturaService";
import collectionsService from "../../services/collectionsService";
import SelectSearch from "react-select-search";
import moment from "moment";

// Variante de JornadaAnioPeriodoCursosMultiAsig SIN el filtro de docente:
// muestra todos los cursos activos y todas las asignaturas activas, ambos en
// modo multi-selección (usado para clonar procesos preventivos hacia varios
// cursos y asignaturas a la vez). El backend se encarga de resolver qué
// combinaciones curso/asignatura tienen carga académica (docente asignado) en
// el año escolar elegido y clona solo hacia esas.
const JornadaAnioPeriodoCursosAsigMulti = forwardRef((props, ref) => {

    const { callback, properties, ReadOnly } = props;
    const [journeyList, setJourneyList] = useState([]);
    const [schoolYearList, setSchoolYearList] = useState([]);
    const [years, setYears] = useState([]);
    const [coursesList, setCoursesList] = useState([]);
    const [courses, setCourses] = useState([]);
    const [periodos, setPeriodos] = useState([]);
    const [subjectsList, setSubjectsList] = useState([]);

    const initData = {
        schoolYearId: '0',
        periodId: '0',
        journeyId: '0',
        courseIds: [],
        schoolSubjectIds: []
    };
    const [formData, setFormData] = useState(initData);

    const cmbCursosId = (properties && properties.id) ? `cmbCursos-${properties.id}` : "cmbCursos";
    const cmbAsignaturasId = (properties && properties.id) ? `cmbAsignaturas-${properties.id}` : "cmbAsignaturas";

    useImperativeHandle(ref, () => ({
        setFormValues(data) {
            setFormData(prevState => ({
                ...prevState,
                ...data
            }));
            if (data && data.schoolYearId !== undefined && String(data.schoolYearId) !== '0') {
                LoadPeriodos(data.schoolYearId, false);
            }
        },
        getCursos() {
            return courses;
        },
        getAsignaturas() {
            return subjectsList;
        },
        getSchoolYears() {
            return years;
        },
        getJourneys() {
            return journeyList;
        }
    }));

    const isActive = (value) => value === true || Number(value) === 1;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
        if (name.includes("schoolYearId")) {
            LoadPeriodos(value);
        } else if (name.includes("journeyId")) {
            LoadYears(value);
            LoadCourses(value);
        }
    };

    // El CSS compartido de SelectSearch (ReactSelectSearch.css) solo oculta el
    // panel de opciones cuando pierde el foco para los combos de selección
    // única; en modo múltiple queda siempre visible (mismo ajuste manual que
    // usa JornadaAnioPeriodoCursosMultiAsig).
    const toggleOptionsList = (cmbId, display) => {
        const cmb = document.getElementById(cmbId);
        if (cmb) {
            const list = cmb.getElementsByClassName("select-search-select")[0];
            if (list) list.style.display = display;
        }
    };

    const toArray = (selectedValues) => Array.isArray(selectedValues)
        ? selectedValues
        : (selectedValues !== undefined && selectedValues !== null ? [selectedValues] : []);

    const handleCoursesChange = (selectedValues) => {
        setFormData(prevState => ({
            ...prevState,
            courseIds: toArray(selectedValues)
        }));
    };

    const handleSubjectsChange = (selectedValues) => {
        setFormData(prevState => ({
            ...prevState,
            schoolSubjectIds: toArray(selectedValues)
        }));
    };

    useEffect(() => {
        if (callback) {
            callback(formData);
        }
    }, [formData]);

    const fetchLoadFilters = async () => {
        try {
            const [journeyResp, yearsResp, coursesResp, subjectsResp] = await Promise.all([
                academicService.getJourneys(),
                academicService.getYears(),
                courseService.getCursos(),
                asignaturaService.getAsignaturas()
            ]);
            setJourneyList(journeyResp ?? []);
            setSchoolYearList(yearsResp ?? []);
            setYears(yearsResp ?? []);

            const activeCourses = (coursesResp ?? []).filter(c => isActive(c.isActive));
            setCoursesList(activeCourses);
            setCourses(activeCourses);

            const activeSubjects = (subjectsResp ?? []).filter(s => isActive(s.isActive));
            setSubjectsList(activeSubjects);
        } catch (error) {
            console.error("Error al obtener listas de formulario:", error);
        }
    };

    useEffect(() => {
        fetchLoadFilters();
        // Los paneles de opciones multi-selección arrancan visibles por defecto
        // salvo que los ocultemos explícitamente; los cerramos al montar.
        toggleOptionsList(cmbCursosId, 'none');
        toggleOptionsList(cmbAsignaturasId, 'none');
    }, []);

    const LoadYears = (journeyId) => {
        const yrs = (journeyId !== "0") ? schoolYearList.filter((op) => String(op.journeyId) === String(journeyId)) : schoolYearList;
        setYears(yrs);
        setPeriodos([]);
        setFormData(prevState => ({
            ...prevState,
            schoolYearId: '0',
            periodId: '0'
        }));
        if (journeyId !== "0") {
            fechtCurrentAnio(yrs);
        }
    };

    const LoadCourses = (journeyId) => {
        const courseFiltred = (journeyId !== "0") ? coursesList.filter((op) => String(op.journeyId) === String(journeyId)) : coursesList;
        setCourses(courseFiltred);
        setFormData(prevState => ({
            ...prevState,
            courseIds: []
        }));
    };

    const fechtCurrentAnio = async (yearsFiltred) => {
        const parameters = await collectionsService.getParameters();
        const currentAnio = parameters.find((op) => String(op.keyName) === "AnioElectivo");
        let preLoad = null;
        if (currentAnio && yearsFiltred.find(y => String(y.schoolYearId) === currentAnio.value)) {
            preLoad = currentAnio.value;
        } else if (yearsFiltred.length === 1) {
            preLoad = yearsFiltred[0].schoolYearId;
        }
        if (preLoad !== null) {
            setFormData(prevState => ({
                ...prevState,
                schoolYearId: preLoad
            }));
            LoadPeriodos(preLoad);
        }
    };

    const LoadPeriodos = async (schoolYearId, autoSelect = true) => {
        if (String(schoolYearId) !== '0') {
            const resYear = await academicService.getYearById(schoolYearId);
            if (resYear) {
                const peri = resYear.periods.filter(p => p.isRecuperation === 0 || !p.isRecuperation);
                setPeriodos(peri);
                if (autoSelect) {
                    let selectedP = null;
                    for (let i = 0; i < peri.length; i++) {
                        const p = peri[i];
                        selectedP = p;
                        const today = moment().toDate();
                        const start = moment(p.startDate).toDate();
                        const end = moment(p.endDate).toDate();
                        if ((today >= start) && (today <= end)) {
                            selectedP = p;
                            break;
                        } else if (today <= start) {
                            selectedP = p;
                            break;
                        }
                    }
                    if (selectedP !== null) {
                        setFormData(prevState => ({
                            ...prevState,
                            periodId: selectedP.academicPeriodId
                        }));
                    }
                }
            } else {
                setPeriodos([]);
            }
        } else {
            setPeriodos([]);
        }
    };

    return (
        <>
            <div className={(properties && properties.className) ? properties.className : "col-md-2 mb-2"} >
                <label className="form-label">Jornada:</label>
                <select
                    id={(properties && properties.id) ? `cmbJourney-${properties.id}` : "cmbJourney"}
                    className="form-control form-select validarForm "
                    name="journeyId"
                    value={formData.journeyId}
                    onChange={handleChange}
                    disabled={ReadOnly}
                >
                    <option value="0"> {(properties && !properties.filtro) ? 'Seleccione' : 'Todos'}</option>
                    {journeyList.map((option) => (
                        <option key={option.journeyId} value={option.journeyId}>
                            {option.name}
                        </option>
                    ))}
                </select>
            </div>
            <div className={(properties && properties.className) ? properties.className : "col-md-2 mb-2"}>
                <label htmlFor={(properties && properties.id) ? `schoolYearId-${properties.id}` : "schoolYearId"}
                    className="form-label">Año escolar:</label>
                <select className="form-control form-select"
                    id={(properties && properties.id) ? `schoolYearId-${properties.id}` : "schoolYearId"}
                    name="schoolYearId"
                    value={formData.schoolYearId}
                    onChange={handleChange}
                    disabled={ReadOnly}>
                    <option value="0" > {(properties && !properties.filtro) ? 'Seleccione' : 'Todos'}</option>
                    {years.map((schoolYear) => (
                        <option key={schoolYear.schoolYearId} value={schoolYear.schoolYearId} >
                            {schoolYear.year}
                        </option>
                    ))}
                </select>
            </div>
            <div className={(properties && properties.className) ? properties.className : "col-md-2 mb-2"}>
                <label htmlFor={(properties && properties.id) ? `periodId-${properties.id}` : "periodId"}
                    className="form-label">Periodo:</label>
                <select className="form-control form-select"
                    id={(properties && properties.id) ? `periodId-${properties.id}` : "periodId"}
                    name="periodId"
                    value={formData.periodId}
                    onChange={handleChange}
                    disabled={ReadOnly}>
                    <option value="0" > {(properties && !properties.filtro) ? 'Seleccione' : 'Todos'}</option>
                    {periodos.map((periodo) => (
                        <option key={periodo.academicPeriodId} value={periodo.academicPeriodId} >
                            {periodo.periodName}
                        </option>
                    ))}
                </select>
            </div>
            <div className={(properties && properties.className) ? properties.className : "col-md-2 mb-2"}>
                <label htmlFor={cmbCursosId} className="form-label">Cursos:</label>
                <div className='container-searcher-general cursos-multi-search'>
                    <SelectSearch
                        options={courses.map(c => ({ name: c.name, value: c.courseId }))}
                        id={cmbCursosId}
                        value={formData.courseIds}
                        multiple
                        search
                        closeOnSelect={false}
                        placeholder="Selecciona uno o varios cursos"
                        onChange={handleCoursesChange}
                        disabled={ReadOnly}
                        onFocus={() => { toggleOptionsList(cmbCursosId, ''); }}
                        onBlur={() => { toggleOptionsList(cmbCursosId, 'none'); }}
                    />
                </div>
            </div>
            <div className={(properties && properties.className) ? properties.className : "col-md-2 mb-2"}>
                <label htmlFor={cmbAsignaturasId} className="form-label">Asignaturas:</label>
                <div className='container-searcher-general cursos-multi-search'>
                    <SelectSearch
                        options={subjectsList.map(s => ({ name: s.name, value: s.schoolSubjectId }))}
                        id={cmbAsignaturasId}
                        value={formData.schoolSubjectIds}
                        multiple
                        search
                        closeOnSelect={false}
                        placeholder="Selecciona una o varias asignaturas"
                        onChange={handleSubjectsChange}
                        disabled={ReadOnly}
                        onFocus={() => { toggleOptionsList(cmbAsignaturasId, ''); }}
                        onBlur={() => { toggleOptionsList(cmbAsignaturasId, 'none'); }}
                    />
                </div>
            </div>
        </>
    );
});

export default JornadaAnioPeriodoCursosAsigMulti;
